import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  Input,
  Label,
  FormFeedback,
  Spinner,
  Alert,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";

import logoLight from "../../assets/images/logo-light.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useDispatch, useSelector } from "react-redux";
import { userResetPassword } from "../../store/auth/resetPassword/actions";

const ResetPasswordPage = (props) => {
  const { t } = useTranslation();
  document.title = `${t("PasswordChange.Create New Password")} | ${t(
    process.env.REACT_APP_NAME
  )}`;

  const dispatch = useDispatch();

  const { loading, resetSuccessMsg, resetErrorMsg } = useSelector(
    (state) => state.ResetPassword
  );
  const { token } = useParams();

  const [passwordShow, setPasswordShow] = useState(false);
  const [confrimPasswordShow, setConfrimPasswordShow] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      password: "",
      passwordConfirm: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Min", { length: 25 }))
        .matches(
          RegExp("(.*[a-z].*)"),
          t("Validations.Password.LowerCaseCheck")
        )
        .matches(
          RegExp("(.*[A-Z].*)"),
          t("Validations.Password.UpperCaseCheck")
        )
        .matches(RegExp("(.*[0-9].*)"), t("Validations.Password.NumberCheck"))
        .required(t("Validations.Password.Required")),
      passwordConfirm: Yup.string()
        .min(8, t("Validations.Common.Min", { length: 8 }))
        .max(25, t("Validations.Common.Min", { length: 25 }))
        .when("password", {
          is: (val) => (val && val.length > 0 ? true : false),
          then: Yup.string().oneOf(
            [Yup.ref("password")],
            t("Validations.Password.Match")
          ),
        })
        .required(t("Validations.ConfirmPassword.Required")),
    }),
    onSubmit: (values) => {
      dispatch(userResetPassword({ ...values, token }, props.history));
    },
  });

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/#" className="d-inline-block auth-logo">
                    <img src={logoLight} alt="" height="20" />
                  </Link>
                </div>
                <p className="mt-3 fs-15 fw-medium">
                  {t("Common.Technical Services at your door step")}
                </p>
              </div>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">
                      {t("PasswordChange.Create New Password")}
                    </h5>
                    <p className="text-muted">
                      {t("PasswordChange.New Password Suggestion")}
                    </p>
                  </div>

                  <div className="p-2">
                    {resetErrorMsg && resetErrorMsg ? (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {t(`Messages.Error.${resetErrorMsg}`)}{" "}
                      </Alert>
                    ) : null}
                    {resetSuccessMsg ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {t(`Messages.Success.${resetSuccessMsg}`)}
                      </Alert>
                    ) : null}
                    <Form
                      onSubmit={validation.handleSubmit}
                      action="/auth-signin-basic"
                    >
                      <div className="mb-3">
                        <Label className="form-label" htmlFor="password">
                          {t("Fields.Password")}
                        </Label>
                        <div className="position-relative auth-pass-inputgroup">
                          <Input
                            type={passwordShow ? "text" : "password"}
                            className="form-control pe-5 password-input"
                            placeholder={t("Fields.Enter Password")}
                            id="password"
                            name="password"
                            value={validation.values.password}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={
                              validation.errors.password &&
                              validation.touched.password
                                ? true
                                : false
                            }
                          />
                          {validation.errors.password &&
                          validation.touched.password ? (
                            <FormFeedback type="invalid">
                              {validation.errors.password}
                            </FormFeedback>
                          ) : null}
                          <Button
                            color="link"
                            onClick={() => setPasswordShow(!passwordShow)}
                            className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                            type="button"
                            id="password-addon"
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </Button>
                        </div>
                        <div id="passwordInput" className="form-text">
                          Must be at least 8 characters.
                        </div>
                      </div>

                      <div className="mb-3">
                        <Label className="form-label" htmlFor="passwordConfirm">
                          {t("Fields.Confirm Password")}
                        </Label>
                        <div className="position-relative auth-pass-inputgroup mb-3">
                          <Input
                            type={confrimPasswordShow ? "text" : "password"}
                            className="form-control pe-5 password-input"
                            placeholder={t("Fields.Enter Confirm Password")}
                            id="passwordConfirm"
                            name="passwordConfirm"
                            value={validation.values.passwordConfirm}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={
                              validation.errors.passwordConfirm &&
                              validation.touched.passwordConfirm
                                ? true
                                : false
                            }
                          />
                          {validation.errors.passwordConfirm &&
                          validation.touched.passwordConfirm ? (
                            <FormFeedback type="invalid">
                              {validation.errors.passwordConfirm}
                            </FormFeedback>
                          ) : null}
                          <Button
                            color="link"
                            onClick={() =>
                              setConfrimPasswordShow(!confrimPasswordShow)
                            }
                            className="position-absolute end-0 top-0 text-decoration-none text-muted password-addon"
                            type="button"
                          >
                            <i className="ri-eye-fill align-middle"></i>
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4">
                        <Button
                          color="success"
                          className="btn-load w-100"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="d-flex align-items-center">
                              <span className="flex-grow-1 me-2">
                                {t("ForgotPassword.Resetting")}...
                              </span>
                              <Spinner
                                size="sm"
                                className="flex-shrink-0"
                                role="status"
                              >
                                {" "}
                                {t("ForgotPassword.Resetting")}...{" "}
                              </Spinner>
                            </span>
                          ) : (
                            <>{t("ForgotPassword.Reset Password")}</>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-4 text-center">
                <p className="mb-0">
                  {t("ForgotPassword.Wait, I remember my password")}...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    {" "}
                    {t("Common.Click here")}{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export default withRouter(ResetPasswordPage);
